/** UUID v4 中用于设置版本号的字节索引。 */
const UUID_VERSION_BYTE_INDEX = 6;

/** UUID v4 中用于设置 RFC 4122 变体的字节索引。 */
const UUID_VARIANT_BYTE_INDEX = 8;

/** Device ID 在三种客户端同步介质中共用的固定键名。 */
export const DEVICE_ID_KEY = 'zl-snowplow_deivce_id';

/** Cookie 的有效期年数。 */
const COOKIE_EXPIRATION_YEARS = 2;

/** Cookie 两年有效期对应的 Max-Age 秒数。 */
const COOKIE_EXPIRATION_SECONDS = 2 * 365 * 24 * 60 * 60;

/** 当前页面上下文中正在执行的 Device ID 初始化任务。 */
let deviceIdInitializationPromise: Promise<string> | null = null;

/**
 * 将单个随机字节转为两位十六进制字符串。
 * @param {number} byte 要格式化的字节。
 * @returns {string} 两位小写十六进制字符串。
 */
function formatByteAsHex(byte: number): string {
  return byte.toString(16).padStart(2, '0');
}

/**
 * 将候选 Device ID 归一化为可用的非空字符串。
 * @param {unknown} value 待读取或校验的原始值。
 * @returns {string|null} 去除首尾空白后的 ID；不可用时返回 null。
 */
function normalizeDeviceId(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const deviceId = value.trim();
  return deviceId || null;
}

/**
 * 生成 Snowplow 使用的 32 位 Device ID。
 * @returns {string} 32 位小写十六进制字符串。
 */
export function generateDeviceId(): string {
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID().replaceAll('-', '').toLowerCase();
    }
  } catch {
    // 继续使用字节级降级，避免受限环境阻断 Device ID 初始化。
  }

  const bytes = new Uint8Array(16);
  try {
    if (typeof globalThis.crypto?.getRandomValues === 'function') {
      globalThis.crypto.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }
  } catch {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[UUID_VERSION_BYTE_INDEX] = (bytes[UUID_VERSION_BYTE_INDEX] & 0x0f) | 0x40;
  bytes[UUID_VARIANT_BYTE_INDEX] = (bytes[UUID_VARIANT_BYTE_INDEX] & 0x3f) | 0x80;

  return Array.from(bytes, formatByteAsHex).join('');
}

/**
 * 从 localStorage 安全读取原始 Device ID。
 * @returns {string|null} 未归一化的字符串；不可用或失败时返回 null。
 */
function readRawLocalStorageDeviceId(): string | null {
  try {
    const value = globalThis.localStorage?.getItem(DEVICE_ID_KEY);
    return typeof value === 'string' ? value : null;
  } catch {
    return null;
  }
}

/**
 * 从 localStorage 安全读取 Device ID。
 * @returns {string|null} 已归一化的 Device ID；不可用或失败时返回 null。
 */
function readLocalStorageDeviceId(): string | null {
  return normalizeDeviceId(readRawLocalStorageDeviceId());
}

/**
 * 将权威 Device ID 安全写入 localStorage。
 * @param {string} deviceId 待保存的权威 Device ID。
 * @returns {boolean} localStorage 是否最终保持目标值。
 */
function writeLocalStorageDeviceId(deviceId: string): boolean {
  try {
    if (readRawLocalStorageDeviceId() !== deviceId) {
      globalThis.localStorage?.setItem(DEVICE_ID_KEY, deviceId);
    }
    return readRawLocalStorageDeviceId() === deviceId;
  } catch {
    return false;
  }
}

/**
 * 从 document.cookie 安全读取原始 Device ID。
 * @returns {string|null} 未归一化的字符串；不可用或失败时返回 null。
 */
function readRawCookieDeviceId(): string | null {
  try {
    const cookieSource = globalThis.document?.cookie;
    if (typeof cookieSource !== 'string') return null;

    const encodedKey = encodeURIComponent(DEVICE_ID_KEY);
    const entry = cookieSource
      .split(';')
      .map(
        /** 去除单个 Cookie 片段首尾空白。 */
        (item) => item.trim(),
      )
      .find(
        /** 判断单个 Cookie 片段是否属于 Device ID。 */
        (item) => item.startsWith(`${encodedKey}=`),
      );
    if (!entry) return null;

    return decodeURIComponent(entry.slice(encodedKey.length + 1));
  } catch {
    return null;
  }
}

/**
 * 从 document.cookie 安全读取 Device ID。
 * @returns {string|null} 已归一化的 Device ID；不可用或失败时返回 null。
 */
function readCookieDeviceId(): string | null {
  return normalizeDeviceId(readRawCookieDeviceId());
}

/**
 * 将权威 Device ID 安全写入 Cookie。
 * @param {string} deviceId 待保存的权威 Device ID。
 * @returns {boolean} Cookie 是否最终保持目标值。
 */
function writeCookieDeviceId(deviceId: string): boolean {
  try {
    if (!globalThis.document) return false;

    const expiresAt = new Date();
    expiresAt.setFullYear(expiresAt.getFullYear() + COOKIE_EXPIRATION_YEARS);
    const secureAttribute = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
    globalThis.document.cookie =
      `${encodeURIComponent(DEVICE_ID_KEY)}=${encodeURIComponent(deviceId)}` +
      `; Max-Age=${COOKIE_EXPIRATION_SECONDS}` +
      `; Expires=${expiresAt.toUTCString()}` +
      `; Path=/; SameSite=Lax${secureAttribute}`;
    return readRawCookieDeviceId() === deviceId;
  } catch {
    return false;
  }
}

/**
 * 从 sessionStorage 安全读取原始 Device ID。
 * @returns {string|null} 未归一化的字符串；不可用或失败时返回 null。
 */
function readRawSessionStorageDeviceId(): string | null {
  try {
    const value = globalThis.sessionStorage?.getItem(DEVICE_ID_KEY);
    return typeof value === 'string' ? value : null;
  } catch {
    return null;
  }
}

/**
 * 从 sessionStorage 安全读取 Device ID。
 * @returns {string|null} 已归一化的 Device ID；不可用或失败时返回 null。
 */
function readSessionStorageDeviceId(): string | null {
  return normalizeDeviceId(readRawSessionStorageDeviceId());
}

/**
 * 将权威 Device ID 安全写入 sessionStorage。
 * @param {string} deviceId 待保存的权威 Device ID。
 * @returns {boolean} sessionStorage 是否最终保持目标值。
 */
function writeSessionStorageDeviceId(deviceId: string): boolean {
  try {
    if (readRawSessionStorageDeviceId() !== deviceId) {
      globalThis.sessionStorage?.setItem(DEVICE_ID_KEY, deviceId);
    }
    return readRawSessionStorageDeviceId() === deviceId;
  } catch {
    return false;
  }
}

/**
 * 按固定优先级读取三个同步介质。
 * @returns {string|null} 第一个可用的同步 Device ID。
 */
function readSynchronousDeviceId(): string | null {
  return readLocalStorageDeviceId() ?? readCookieDeviceId() ?? readSessionStorageDeviceId();
}

/**
 * 将权威 ID 同步写入三个同步介质。
 * @param {string} deviceId 当前页面使用的权威 Device ID。
 * @returns {void}
 */
function synchronizeSynchronousDeviceId(deviceId: string): void {
  writeLocalStorageDeviceId(deviceId);
  writeCookieDeviceId(deviceId);
  writeSessionStorageDeviceId(deviceId);
}

/**
 * 解析当前页面权威 Device ID，并同步三种客户端介质。
 * @returns {string} 当前 Provider 生命周期应固定使用的 ID。
 */
function resolveAndSynchronizeDeviceId(): string {
  const deviceId = readSynchronousDeviceId() ?? generateDeviceId();
  synchronizeSynchronousDeviceId(deviceId);
  return deviceId;
}

/**
 * 获取或创建 Device ID，并在三种同步介质完成写入尝试后返回。
 * @returns {Promise<string>} 当前设备的稳定 Device ID。
 */
export async function getOrCreateDeviceId(): Promise<string> {
  if (!deviceIdInitializationPromise) {
    deviceIdInitializationPromise = Promise.resolve().then(
      /** 延迟执行解析器，确保同步 getter 重入时已建立共享 Promise。 */
      () => resolveAndSynchronizeDeviceId(),
    );
  }

  const currentPromise = deviceIdInitializationPromise;
  try {
    return await currentPromise;
  } finally {
    if (deviceIdInitializationPromise === currentPromise) {
      deviceIdInitializationPromise = null;
    }
  }
}
